import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import UserContext from '../context/UserContext.js'
import useLearnerContext from '../hooks/useLearnerContext'
import {
  MY_SENTENCE_BUILDS_QUERY, SENTENCE_BUILD_QUERY, SENTENCE_MAP_QUERY,
  START_SENTENCE_BUILD_MUTATION, SAVE_SENTENCE_BUILD_MUTATION,
} from '../query/gqlQueries.js'
import languages from '../languages'
import voices from '../speech/voices'
import { speakTracked, stopSpeaking } from '../speech/speak'
import useSpeechToText from '../speech/useSpeechToText'
import { mergeTranscript, wordUnits, tokenizeLine, tokenizeTranscript, tokenMatches } from '../speech/flowMatch'
import s from './SentenceBuilderView.module.scss'

// The sentence builder. Say (or type) what you want to say in your own
// language; the Mac Studio says it in the language you're learning, split into
// the blocks a beginner builds it from — for Turkish, Selinay's safe map
// WHO + WHEN + WHERE + WHAT + VERB. Then you build it a block at a time:
// "Who?" — pick the right form among a couple of wrong endings (or say it),
// see why it's that form, and on to the next, until the whole sentence stands.
// Then hear it, say it all with the words lighting up, and keep it.

// Each block's question, and its colour (Apple's system colours).
export const ROLES = {
  who: { ask: 'Who?', color: '#64d2ff' },
  when: { ask: 'When?', color: '#bf5af2' },
  where: { ask: 'Where?', color: '#40c8e0' },
  what: { ask: 'What?', color: 'var(--frost-amber)' },
  verb: { ask: 'Doing what?', color: 'var(--frost-right)' },
  is: { ask: 'Is what?', color: '#ff375f' },
  how: { ask: 'How?', color: 'var(--frost-amber)' },
  with: { ask: 'With whom?', color: '#5e5ce6' },
  to: { ask: 'To whom?', color: '#ac8e68' },
  why: { ask: 'Why?', color: '#ff6482' },
  ask: { ask: 'Question', color: '#66d4cf' },
  not: { ask: 'Not', color: 'var(--frost-wrong)' },
  other: { ask: 'And…', color: 'var(--frost-muted)' },
}
const roleOf = (r) => ROLES[r] || ROLES.other

const voiceFor = (lang) => voices[lang] || voices.eng
const cpsFor = (lang) => (['kor', 'jpn', 'cmn'].includes(lang) ? 4.5 : 13)
const sayable = (text) => String(text || '').replace(/["“”«»„‹›]/g, ' ')
const POLL_MS = 1500
const GIVE_UP_MS = 4 * 60 * 1000
const LANG_KEY = 'sentenceBuilder.lang'

// Every word of `said` heard in `transcript`, in any order.
const saidIn = (transcript, said, lang) => {
  const want = tokenizeLine(said, lang).map(t => t.norm)
  const got = tokenizeTranscript(transcript, lang)
  return want.length > 0 && want.every(w => got.some(g => tokenMatches(g, w, lang)))
}

export default function SentenceBuilderView({ voiceSpeed, lang: startLang, onClose }) {
  const { username } = useContext(UserContext)
  const { native, learning } = useLearnerContext(username)
  const client = useApolloClient()
  const myLangs = (learning || []).filter(l => l && l.code)
  const nativeCode = native?.[0]?.code || 'eng'
  const [lang, setLangState] = useState(() => {
    if (startLang) return startLang
    try { return localStorage.getItem(LANG_KEY) || '' } catch { return '' }
  })
  const target = lang || myLangs[0]?.code || 'tur'
  const setLang = (c) => { setLangState(c); try { localStorage.setItem(LANG_KEY, c) } catch { /* private window */ } }

  const [phase, setPhase] = useState('ask') // ask | making | build | done
  const [text, setText] = useState('')
  const [build, setBuild] = useState(null)
  const [error, setError] = useState('')
  const alive = useRef(true)
  useEffect(() => () => { alive.current = false; stopSpeaking() }, [])

  const [start] = useMutation(START_SENTENCE_BUILD_MUTATION)

  const open = (b) => { setBuild(b); setError(''); setPhase('build') }

  const go = async () => {
    const said = text.trim()
    if (!said) return
    setError('')
    setPhase('making')
    try {
      let b = (await start({ variables: { lang: target, text: said } })).data.startSentenceBuild
      const t0 = Date.now()
      while (alive.current && b.status === 'making' && Date.now() - t0 < GIVE_UP_MS) {
        await new Promise(r => setTimeout(r, POLL_MS))
        b = (await client.query({ query: SENTENCE_BUILD_QUERY, variables: { id: b.id }, fetchPolicy: 'network-only' })).data.sentenceBuild
        if (!b) throw new Error('That sentence went missing — try again.')
      }
      if (!alive.current) return
      if (b.status === 'ready' && b.blocks.length) open(b)
      else { setError(b.error || 'That took too long — try again in a moment.'); setPhase('ask') }
    } catch (e) {
      if (!alive.current) return
      setError(/Cannot query field/i.test(e.message) ? 'The server hasn’t been updated with the sentence builder yet.' : e.message)
      setPhase('ask')
    }
  }

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && phase === 'ask') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase, onClose])

  return (
    <div className={s.view}>
      <div className={s.inner}>
        <div className={s.top}>
          <button className={`${s.btn} ${s.small}`} onClick={() => {
            stopSpeaking()
            if (phase === 'ask' || phase === 'making') onClose()
            else setPhase('ask')
          }}>{phase === 'ask' || phase === 'making' ? '← Close' : '← New sentence'}</button>
          <div className={s.title}>Sentence Builder · {languages[target] || target}</div>
          <span style={{ width: 110 }} />
        </div>

        {phase === 'ask' && (
          <Ask lang={target} nativeCode={nativeCode} myLangs={myLangs} setLang={setLang} fixed={!!startLang}
            text={text} setText={setText} onGo={go} error={error} onOpen={open} />
        )}
        {phase === 'making' && <Making text={text} lang={target} />}
        {(phase === 'build' || phase === 'done') && build && (
          <Build key={build.id} build={build} voiceSpeed={voiceSpeed} done={phase === 'done'}
            onDone={() => setPhase('done')} onAgain={() => setPhase('build')}
            onNew={() => { setText(''); setPhase('ask') }} />
        )}
      </div>
    </div>
  )
}

// ── What do you want to say? ───────────────────────────────────────────────
function Ask({ lang, nativeCode, myLangs, setLang, fixed, text, setText, onGo, error, onOpen }) {
  const { data: mapData } = useQuery(SENTENCE_MAP_QUERY, { variables: { lang }, errorPolicy: 'ignore' })
  const { data: recentData } = useQuery(MY_SENTENCE_BUILDS_QUERY, { variables: { lang, limit: 8 }, fetchPolicy: 'network-only', errorPolicy: 'ignore' })
  const recent = (recentData?.mySentenceBuilds || []).filter(b => b.status === 'ready' && b.blocks.length)
  const map = mapData?.sentenceMap || []
  const stt = useSpeechToText(nativeCode, { onFinal: (t) => setText(prev => (prev ? `${prev} ${t}` : t)) })
  const inputRef = useRef(null)
  useEffect(() => { inputRef.current?.focus() }, [])

  return (
    <div className={s.ask}>
      <div>
        <h1 className={s.hero}>What do you want to say?</h1>
        <p className={s.sub}>Say it or type it in {languages[nativeCode] || 'your language'} — then we’ll build it in {languages[lang] || lang}, one block at a time.</p>
      </div>

      {!fixed && myLangs.length > 1 && (
        <div className={s.langs}>
          {myLangs.map(l => (
            <button key={l.code} className={`${s.btn} ${s.chip} ${l.code === lang ? s.chipOn : ''}`} onClick={() => setLang(l.code)}>
              {l.name || languages[l.code] || l.code}
            </button>
          ))}
        </div>
      )}

      <div className={s.inputRow}>
        <textarea ref={inputRef} rows={2} className={s.input} value={text} placeholder="e.g. I’m drinking coffee at the café with my friend"
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onGo() } }} />
        {stt.supported && (
          <button className={`${s.btn}`} onClick={() => (stt.listening ? stt.stop() : stt.start())} title="Say it">
            {stt.listening ? '■ Stop' : '🎙 Say it'}
          </button>
        )}
        <button className={`${s.btn} ${s.prominent}`} disabled={!text.trim()} onClick={onGo}>Build it →</button>
      </div>
      <div className={s.interim}>{stt.listening ? (stt.interim || 'Listening…') : ''}</div>
      {error && <div className={s.error}>{error}</div>}

      {map.length > 0 && (
        <div className={s.platter}>
          <div className={s.label} style={{ marginBottom: '0.6rem' }}>The safe beginner map in {languages[lang] || lang}</div>
          <RoleMap roles={map} />
          <div className={s.sub} style={{ fontSize: '0.95rem', marginTop: '0.6rem' }}>
            Build in small blocks instead of searching for the whole sentence at once. Word order can change for emphasis, but this order is a clear, natural start.
          </div>
        </div>
      )}

      {recent.length > 0 && (
        <div>
          <div className={s.label} style={{ marginBottom: '0.6rem' }}>Your sentences</div>
          <div className={s.recent}>
            {recent.map(b => (
              <button key={b.id} className={`${s.btn} ${s.recentTile}`} onClick={() => onOpen(b)}>
                <span>{b.sentence}</span>
                <span className={s.recentSub}>{b.text}{b.saved ? ' · ★' : ''}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function RoleMap({ roles }) {
  return (
    <div className={s.map}>
      {roles.map((r, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className={s.plus}>+</span>}
          <span className={s.mapRole} style={{ '--role': roleOf(r).color }}>{r === 'verb' ? 'VERB' : roleOf(r).ask.replace('?', '').toUpperCase()}</span>
        </React.Fragment>
      ))}
    </div>
  )
}

function Making({ text, lang }) {
  const { data } = useQuery(SENTENCE_MAP_QUERY, { variables: { lang }, errorPolicy: 'ignore' })
  const map = data?.sentenceMap || ['who', 'when', 'where', 'what', 'verb']
  return (
    <div className={s.working}>
      <div className={s.label}>You want to say</div>
      <div className={s.wantText}>{text}</div>
      <div className={s.slots}>
        {map.map((r, i) => (
          <div key={i} className={`${s.slot} ${s.shimmerSlot}`} style={{ '--role': roleOf(r).color, animationDelay: `${i * 0.15}s` }}>
            <span className={s.slotRole}>{roleOf(r).ask}</span>
            <span className={s.slotText}>…</span>
          </div>
        ))}
      </div>
      <div className={s.sub}>Building it in {languages[lang] || lang} on the Mac Studio…</div>
    </div>
  )
}

// ── Building it, block by block ─────────────────────────────────────────────
function Build({ build, voiceSpeed, done, onDone, onAgain, onNew }) {
  const lang = build.lang
  const blocks = build.blocks
  const [step, setStep] = useState(done ? blocks.length : 0)
  const [wrong, setWrong] = useState([])       // options tried wrongly on this step
  const [shown, setShown] = useState(null)     // the block just built, with its note
  const [mistakes, setMistakes] = useState(0)
  const [round, setRound] = useState(0)        // reshuffles the options when building again
  const [listening, setListening] = useState(false)
  const timer = useRef(null)
  useEffect(() => () => clearTimeout(timer.current), [])

  const cur = step < blocks.length ? blocks[step] : null
  const options = useMemo(() => {
    if (!cur) return []
    const o = cur.options.length ? cur.options.slice() : [cur.said]
    // A fresh order each time round, so it's the form you pick, not the place.
    for (let i = o.length - 1; i > 0 && round > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [o[i], o[j]] = [o[j], o[i]] }
    return o
  }, [cur, round])

  const next = () => {
    clearTimeout(timer.current)
    setShown(null)
    setWrong([])
    const n = step + 1
    setStep(n)
    if (n >= blocks.length) { setListening(false); onDone() }
  }

  const pick = (o) => {
    if (!cur || shown) return
    if (o.trim().toLowerCase() === cur.said.trim().toLowerCase()) {
      setShown(step)
      speakTracked(sayable(cur.said), voiceFor(lang), voiceSpeed, { cps: cpsFor(lang) })
      // Time to read why it's this form; straight on when there's nothing to read.
      timer.current = setTimeout(next, cur.note || cur.base ? 3200 : 1100)
    } else if (!wrong.includes(o)) {
      setWrong(w => [...w, o])
      setMistakes(m => m + 1)
    }
  }

  // Say the block instead of picking it.
  const stt = useSpeechToText(lang, { onFinal: (t) => { if (cur && !shown && saidIn(t, cur.said, lang)) pick(cur.said) } })
  useEffect(() => { if (cur && !shown && stt.interim && saidIn(stt.interim, cur.said, lang)) pick(cur.said) }, [stt.interim]) // eslint-disable-line
  useEffect(() => {
    if (!listening || done) { if (stt.listening) stt.stop(); return }
    if (!stt.listening) { const t = setTimeout(() => stt.start(), 250); return () => clearTimeout(t) }
  }, [listening, stt.listening, done]) // eslint-disable-line

  useEffect(() => {
    if (done) return
    const onKey = (e) => {
      if (e.target && /TEXTAREA|INPUT/.test(e.target.tagName)) return
      const n = Number(e.key)
      if (n >= 1 && n <= options.length) pick(options[n - 1])
      else if ((e.key === 'Enter' || e.key === ' ') && shown !== null) { e.preventDefault(); next() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const again = () => { setStep(0); setWrong([]); setShown(null); setMistakes(0); setRound(r => r + 1); onAgain() }

  return (
    <>
      <div className={s.want}>
        <div className={s.label}>You want to say</div>
        <div className={s.wantText}>{build.text}</div>
      </div>

      <div className={s.slots}>
        {blocks.map((b, i) => {
          const built = i < step || i === shown || done
          const current = !done && i === step && shown === null
          return (
            <div key={`${i}-${round}`} className={`${s.slot} ${current ? s.slotCurrent : ''} ${built ? s.slotDone : ''} ${b.dropped ? s.dropped : ''}`}
              style={{ '--role': roleOf(b.role).color }}>
              <span className={s.slotRole}>{roleOf(b.role).ask}</span>
              <span className={s.slotText}>{built ? b.said : ''}</span>
              <span className={s.slotMeaning}>{b.meaning || ''}</span>
              {b.dropped && <span className={s.dropTag}>can be left out</span>}
            </div>
          )
        })}
      </div>

      {!done && cur && (
        <div className={s.prompt}>
          <div className={s.question} style={{ color: roleOf(cur.role).color }}>{roleOf(cur.role).ask}</div>
          {cur.meaning && <div className={s.hintText}>“{cur.meaning}”</div>}
          <div className={s.options}>
            {options.map((o, i) => {
              const isWrong = wrong.includes(o)
              const isRight = shown !== null && o === cur.said
              return (
                <button key={`${o}-${i}`} autoFocus={i === 0} disabled={shown !== null && !isRight}
                  className={`${s.btn} ${s.option} ${isWrong ? s.optionWrong : ''} ${isRight ? s.optionRight : ''}`}
                  onClick={() => pick(o)}>
                  <span className={s.optionKey}>{i + 1}</span>{o}
                </button>
              )
            })}
          </div>
          {shown !== null && (cur.note || cur.base) && (
            <div className={s.note}>
              <b>{cur.said}</b>{cur.base ? <span className={s.base}> ← {cur.base}</span> : null}{cur.note ? <> — {cur.note}</> : null}
            </div>
          )}
          {shown === null && wrong.length >= 2 && cur.note && <div className={s.note}>💡 {cur.note}</div>}
          {shown !== null && <button className={`${s.btn} ${s.small}`} onClick={next}>Next →</button>}
          {stt.supported && shown === null && (
            <div className={s.mic}>
              <button className={`${s.btn} ${s.small}`} onClick={() => setListening(l => !l)}>{listening ? '🎙 Listening — say the block' : '🎙 Say it instead'}</button>
              {listening && stt.interim && <span>{stt.interim}</span>}
            </div>
          )}
        </div>
      )}

      {done && <Finished build={build} voiceSpeed={voiceSpeed} mistakes={mistakes} onAgain={again} onNew={onNew} />}
    </>
  )
}

// ── The whole sentence ──────────────────────────────────────────────────────
function Finished({ build, voiceSpeed, mistakes, onAgain, onNew }) {
  const lang = build.lang
  const shape = useMemo(() => wordUnits(build.sentence, lang), [build.sentence, lang])
  const [states, setStates] = useState(() => shape.tokens.map(() => 'pending'))
  const [saying, setSaying] = useState(null)
  const [saved, setSaved] = useState(build.saved)
  const [save, { loading: saving }] = useMutation(SAVE_SENTENCE_BUILD_MUTATION)
  const [micOn, setMicOn] = useState(false)
  const speakingRef = useRef(false)
  const cancelRef = useRef(null)

  const hear = (text = build.sentence, track = true) => {
    cancelRef.current?.()
    speakingRef.current = true
    const r = speakTracked(sayable(text), voiceFor(lang), voiceSpeed, {
      cps: cpsFor(lang),
      onBoundary: track ? ({ charIndex }) => {
        let u = null
        for (let i = 0; i < shape.units.length; i++) if (shape.units[i].start <= charIndex) u = i
        setSaying(u)
      } : undefined,
      onEnd: () => { setSaying(null); setTimeout(() => { speakingRef.current = false }, 400) },
    })
    cancelRef.current = r.cancel
  }
  useEffect(() => { const t = setTimeout(() => hear(), 450); return () => { clearTimeout(t); cancelRef.current?.() } }, []) // eslint-disable-line

  const stt = useSpeechToText(lang, { onFinal: (t) => heard(t) })
  const heard = (t) => {
    if (speakingRef.current) return
    setStates(prev => mergeTranscript(prev, t, shape.tokens, lang, false))
  }
  useEffect(() => { if (stt.interim) heard(stt.interim) }, [stt.interim]) // eslint-disable-line
  useEffect(() => {
    if (!micOn) { if (stt.listening) stt.stop(); return }
    if (!stt.listening) { const t = setTimeout(() => stt.start(), 250); return () => clearTimeout(t) }
  }, [micOn, stt.listening]) // eslint-disable-line
  const allSaid = states.length > 0 && states.every(x => x === 'hit')
  useEffect(() => { if (allSaid) setMicOn(false) }, [allSaid])

  const keep = async () => {
    try { await save({ variables: { id: build.id } }); setSaved(true) } catch (e) { window.alert(e.message) }
  }

  const heardUnit = (u) => u.to > u.from && states.slice(u.from, u.to).every(x => x === 'hit')

  return (
    <div style={{ animation: 'none' }}>
      <div className={s.praise}>{mistakes === 0 ? '✓ Built it first time!' : '✓ Built it!'}</div>
      <div className={s.final}>
        {shape.units.map((u, i) => (
          <React.Fragment key={i}>
            {i > 0 && shape.spaced ? ' ' : ''}
            <span className={`${s.word} ${i === saying ? s.wordSaying : ''} ${heardUnit(u) ? s.wordHeard : ''}`}>{u.raw}</span>
          </React.Fragment>
        ))}
      </div>
      {build.meaning && <div className={s.finalMeaning}>{build.meaning}</div>}
      {allSaid && <div className={s.praise} style={{ marginTop: '0.8rem' }}>All of it — well said!</div>}

      <div className={s.actions}>
        <button className={s.btn} onClick={() => hear()}>🔊 Hear it</button>
        {stt.supported && <button className={s.btn} onClick={() => { setStates(shape.tokens.map(() => 'pending')); setMicOn(m => !m) }}>{micOn ? '■ Stop' : '🎙 Say it all'}</button>}
        <button className={s.btn} disabled={saved || saving} onClick={keep}>{saved ? '★ In your phrases' : '☆ Keep it'}</button>
        <button className={s.btn} onClick={onAgain}>↻ Build it again</button>
        <button className={`${s.btn} ${s.prominent}`} autoFocus onClick={onNew}>Something else →</button>
      </div>
      {micOn && <div className={s.mic}>{stt.interim || 'Listening — say the whole sentence'}</div>}

      <div className={s.grid}>
        <div className={s.platter}>
          <div className={s.label} style={{ marginBottom: '0.5rem' }}>How it’s built</div>
          <RoleMap roles={build.blocks.map(b => b.role)} />
          <div style={{ marginTop: '0.6rem' }}>
            {build.blocks.map((b, i) => (
              <div key={i} className={s.line}>
                <button className={`${s.btn} ${s.hear}`} onClick={() => hear(b.said, false)}>🔊</button>
                <div>
                  <div className={s.lineSaid}><span style={{ color: roleOf(b.role).color }}>{b.said}</span>{b.base ? <span className={s.lineSub}> ← {b.base}</span> : null}</div>
                  <div className={s.lineSub}>{[b.meaning, b.note].filter(Boolean).join(' — ')}{b.dropped ? ' · can be left out' : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {build.variations.length > 0 && (
            <div className={s.platter}>
              <div className={s.label} style={{ marginBottom: '0.5rem' }}>Other ways to say it</div>
              {build.variations.map((v, i) => (
                <div key={i} className={s.line}>
                  <button className={`${s.btn} ${s.hear}`} onClick={() => hear(v.said, false)}>🔊</button>
                  <div>
                    <div className={s.lineSaid}>{v.said}</div>
                    <div className={s.lineSub}>{[v.meaning, v.note].filter(Boolean).join(' — ')}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {build.tip && (
            <div className={s.platter}>
              <div className={s.label} style={{ marginBottom: '0.4rem' }}>Build others like it</div>
              <div style={{ fontSize: '1.02rem', lineHeight: 1.5 }}>{build.tip}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
